import "core-js/modules/es.array.push.js";
import { ref, onMounted, defineComponent, onBeforeMount, onUnmounted } from 'vue';
import { useSSO } from '@/use/sso';
import { useRules } from '@/utils/rules';
import { useSegment } from '@/use/segment';
import { useSession } from '@/use/session';
import { GOOGLE_RECAPTCHA_SITE_KEY } from '@/constants/env';
import { load } from 'recaptcha-v3';
import { useOnboarding } from '@/use/onboarding';
import { useOTP } from '@/use/otp';
import { useRouter } from 'vue-router/composables';
import { useToasts } from '@/use/toasts';
import { ERROR } from '@/constants/icons';
import { EMAIL_OR_PHONE_REQ, WRONG_OTP_ERROR } from '@/constants/authorization';
export default defineComponent({
  setup: () => {
    const {
      loadInvitedCompanyDetails
    } = useOnboarding();
    const {
      addToast
    } = useToasts();
    const password = ref('');
    const phoneOrEmail = ref('');
    const inputData = ref({
      inputType: '',
      inputValue: ''
    });
    const isIframeLoaded = ref(false);
    const rememberMe = ref(false);
    const otpFor = ref('login');
    const {
      trackEvent
    } = useSegment();
    const {
      session,
      login,
      checkContactExisting
    } = useSession();
    const {
      presenceRule
    } = useRules();
    const otpModalRef = ref(null);
    const updatePasswordModalRef = ref(null);
    const forgotPasswordModalRef = ref(null);
    const phoneOrEmailRef = ref(null);
    const recaptcha = ref(null);
    const recaptchaToken = ref(null);
    const phoneOrEmailErrorMessage = ref('');
    const reCAPTCHASiteKey = ref(GOOGLE_RECAPTCHA_SITE_KEY);
    const errorMessage = ref(null);
    const {
      phoneNumberRef,
      userObj,
      ssoRedirectURL,
      showLoader
    } = useSSO();
    const {
      sendAuthenticationCode
    } = useOTP();
    const router = useRouter();
    const {
      currentRoute: route
    } = router;
    const onLoad = () => {
      isIframeLoaded.value = true;
    };
    const validPhoneOrEmail = ref(false);
    const signUpNowHandler = () => {
      router.push('/signup');
    };
    const validateEmailOrPhone = async () => {
      var _phoneOrEmailRef$valu, _phoneOrEmailRef$valu2;
      await generateRecaptcha('check_contact_details');
      const error = {
        errorText: '',
        isError: false
      };
      inputData.value = {
        inputType: phoneOrEmailRef.value.isPhoneNumber ? 'phone' : 'email',
        inputValue: phoneOrEmail.value
      };
      if ((_phoneOrEmailRef$valu = phoneOrEmailRef.value) !== null && _phoneOrEmailRef$valu !== void 0 && _phoneOrEmailRef$valu.validate()) {
        await checkContactExisting(inputData.value.inputValue, recaptchaToken.value, error, inputData.value.inputType);
      }
      phoneOrEmailErrorMessage.value = error.errorText;
      validPhoneOrEmail.value = phoneOrEmail.value !== '' && !phoneOrEmailErrorMessage.value && ((_phoneOrEmailRef$valu2 = phoneOrEmailRef.value) === null || _phoneOrEmailRef$valu2 === void 0 ? void 0 : _phoneOrEmailRef$valu2.validate());
    };
    const submitCode = async code => {
      if (otpFor.value === 'reset-password') {
        var _otpModalRef$value, _updatePasswordModalR;
        // open modal for new password and confirm password
        (_otpModalRef$value = otpModalRef.value) === null || _otpModalRef$value === void 0 ? void 0 : _otpModalRef$value.close();
        updatePasswordModalRef.value.otpCode = code;
        updatePasswordModalRef.value.emailOrPhone = inputData.value;
        (_updatePasswordModalR = updatePasswordModalRef.value) === null || _updatePasswordModalR === void 0 ? void 0 : _updatePasswordModalR.show();
      } else {
        showLoader(true);
        const user = {
          login: inputData.value.inputValue,
          password: code,
          remember: false,
          source: 'otp'
        };
        const token = route.query.token || '';
        trackEvent('login', {
          submitMethod: 'otp'
        });
        const res = await login(user, token);
        if (res !== null && res !== void 0 && res.error) {
          errorMessage.value = res.error;
        }
        showLoader(false);
        if (session.response.status === 401) {
          errorMessage.value = WRONG_OTP_ERROR;
        }
      }
    };
    // ssoCallBack - function call when sso call back
    const ssoCallBack = async data => {
      var _data$data;
      if (((_data$data = data.data) === null || _data$data === void 0 ? void 0 : _data$data.resource) !== 'sso') return;
      userObj.value = data.data.data;
      if (data.data.status === 404) {
        var _phoneNumberRef$value;
        (_phoneNumberRef$value = phoneNumberRef.value) === null || _phoneNumberRef$value === void 0 ? void 0 : _phoneNumberRef$value.show();
      } else if (data.data.status === 200) {
        const token = route.query.token || '';
        const res = await login({
          login: '',
          password: ''
        }, token, true);
        if (res !== null && res !== void 0 && res.error) {
          addToast({
            color: 'error',
            prependIcon: ERROR,
            message: res.error
          });
        }
      } else {
        addToast({
          color: 'error',
          prependIcon: ERROR,
          message: 'Something went wrong, please try again later.'
        });
      }
    };
    const generateRecaptcha = async action => {
      recaptchaToken.value = await recaptcha.value.execute(action);
    };
    onMounted(async () => {
      recaptcha.value = await load(reCAPTCHASiteKey.value);
      window.addEventListener('message', ssoCallBack, false);
      setTimeout(() => {
        if (phoneOrEmail.value.length) {
          var _phoneOrEmailRef$valu3;
          validPhoneOrEmail.value = (_phoneOrEmailRef$valu3 = phoneOrEmailRef.value) === null || _phoneOrEmailRef$valu3 === void 0 ? void 0 : _phoneOrEmailRef$valu3.validate();
        }
      }, 500);
    });
    onUnmounted(() => {
      // Remove SSO post message listner
      window.removeEventListener('message', ssoCallBack);
    });
    onBeforeMount(async () => {
      var _route$query;
      if ((_route$query = route.query) !== null && _route$query !== void 0 && _route$query.token) {
        phoneOrEmail.value = await loadInvitedCompanyDetails(route.query.token);
        inputData.value.inputValue = phoneOrEmail.value;
      }
    });
    const submitLogin = async () => {
      if (password.value.length > 1) {
        showLoader(true);
        const user = {
          login: phoneOrEmail.value,
          password: password.value,
          remember: rememberMe.value
        };
        const token = route.query.token || '';
        trackEvent('login', {
          submitMethod: 'password'
        });
        const res = await login(user, token);
        if (res !== null && res !== void 0 && res.error) {
          addToast({
            color: 'error',
            prependIcon: ERROR,
            message: res.error
          });
        }
        showLoader(false);
      } else {
        // OTP flow
        sendOTP('sign_in');
      }
    };
    const sendOtpToUser = async (isResend = false, token) => {
      showLoader(true);
      phoneOrEmail.value = inputData.value.inputValue;
      if (phoneOrEmail.value !== '') {
        const data = await sendAuthenticationCode(token, inputData.value, isResend, otpFor.value);
        if (data && data.success) {
          var _forgotPasswordModalR;
          (_forgotPasswordModalR = forgotPasswordModalRef.value) === null || _forgotPasswordModalR === void 0 ? void 0 : _forgotPasswordModalR.close();
          if (otpFor.value === 'reset_password') {
            var _updatePasswordModalR2;
            updatePasswordModalRef.value.inputData = inputData.value;
            (_updatePasswordModalR2 = updatePasswordModalRef.value) === null || _updatePasswordModalR2 === void 0 ? void 0 : _updatePasswordModalR2.show();
          } else {
            var _otpModalRef$value2;
            (_otpModalRef$value2 = otpModalRef.value) === null || _otpModalRef$value2 === void 0 ? void 0 : _otpModalRef$value2.show();
          }
        }
      }
      showLoader(false);
    };
    const forgotPasswordSendOTP = async data => {
      inputData.value = data;
      sendOTP('reset_password');
    };
    const sendOTP = async (type, isResend = false) => {
      recaptchaToken.value = await recaptcha.value.execute('send_otp');
      otpFor.value = type;
      errorMessage.value = null;
      sendOtpToUser(isResend, recaptchaToken.value);
    };
    return {
      password,
      phoneOrEmail,
      validPhoneOrEmail,
      ssoRedirectURL,
      showLoader,
      isIframeLoaded,
      onLoad,
      rememberMe,
      presenceRule,
      signUpNowHandler,
      submitCode,
      otpFor,
      updatePasswordModalRef,
      otpModalRef,
      forgotPasswordModalRef,
      submitLogin,
      phoneOrEmailRef,
      phoneOrEmailErrorMessage,
      validateEmailOrPhone,
      forgotPasswordSendOTP,
      errorMessage,
      sendOTP,
      EMAIL_OR_PHONE_REQ
    };
  }
});